import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { can, useOrg } from "../org";
import { CareState, Field, RhythmPicker, Select, SourceTag, displayName, fromPco } from "../people/bits";

const ledBy = (g) => {
  const names = [...g.leaders.map((l) => l.name), ...(g.pending_leaders || []).map((l) => `${l.name} (Pending)`)];
  return names.length ? `Led by ${names.join(", ")}` : "No leader yet";
};

const GroupForm = ({ group, onClose, onSaved }) => {
  const { api } = useOrg();
  const [f, setF] = useState({ name: group?.name || "", description: group?.description || "", rhythm_days: group?.rhythm_days ?? null, leader_ids: group?.leader_ids || [] });
  const [leaders, setLeaders] = useState([]);
  useEffect(() => { api("/caregivers").then(setLeaders).catch(() => {}); }, [api]);
  const toggle = (id) => setF((x) => ({ ...x, leader_ids: x.leader_ids.includes(id) ? x.leader_ids.filter((i) => i !== id) : [...x.leader_ids, id] }));
  const save = () => api(group ? `/groups/${group.id}` : "/groups", { method: group ? "PATCH" : "POST", body: f })
    .then((g) => { toast.success(group ? "Group saved" : `${g.name} created`); onSaved(g); }).catch((e) => toast.error(e.message));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-paper max-w-lg max-h-[92dvh] overflow-y-auto" data-testid="group-form">
        <DialogTitle className="t-name-lg">{group ? "Edit group" : "New Care Group"}</DialogTitle>
        <DialogDescription className="t-meta">Groups organize people. Each person still has one Assigned Care Leader.</DialogDescription>
        <Field label="Name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="field h-12 px-3" placeholder="e.g. Homebound" data-testid="group-form-name" /></Field>
        <Field label="Description (optional)"><input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="field h-12 px-3" data-testid="group-form-description" /></Field>
        <Field label="Care Rhythm for this group"><RhythmPicker value={f.rhythm_days} onChange={(v) => setF({ ...f, rhythm_days: v })} allowNone testid="group-form-rhythm" /></Field>
        <p className="t-meta -mt-2">Leave as None to use the church default. If someone is in several groups, the shortest rhythm applies.</p>
        <Field label="Group leaders">
          <div className="flex flex-wrap gap-2">
            {leaders.map((l) => <button type="button" key={l.id} aria-pressed={f.leader_ids.includes(l.id)} onClick={() => toggle(l.id)} className="choice h-11 px-4" data-testid={`group-form-leader-${l.id}`}>{l.name}</button>)}
          </div>
        </Field>
        <p className="t-meta -mt-2">Leaders can see the people in this group. Leading a group never grants Confidential Access.</p>
        <button disabled={!f.name.trim()} onClick={save} className="btn btn-primary w-full" data-testid="group-form-save">{group ? "Save changes" : "Create group"}</button>
      </DialogContent>
    </Dialog>
  );
};

export function Groups() {
  const { api, membership, onTenantError } = useOrg();
  const c = can(membership);
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api("/groups").then(setGroups).catch((e) => { onTenantError(e); toast.error(e.message); });
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="groups-screen">
      <div className="flex items-end justify-between gap-4">
        <div><p className="t-label">{membership.org_name}</p><h1 className="t-greeting mt-3">Care Groups</h1></div>
        {c.directory && <button onClick={() => setAdding(true)} className="btn btn-primary btn-sm shrink-0" data-testid="groups-add-btn"><Plus size={18} /> New group</button>}
      </div>
      <p className="t-lead mt-3">{c.leader ? "Groups you lead." : "Ways your church organizes people for care."}</p>
      <div className="rule-list border-y border-line mt-10" data-testid="groups-list">
        {groups?.map((g) => (
          <button key={g.id} onClick={() => navigate(`/groups/${g.id}`)} className="w-full text-left py-5 flex justify-between gap-4" data-testid={`group-row-${g.id}`}>
            <div className="min-w-0"><p className="t-name truncate flex items-center gap-2">{g.name}<SourceTag x={g} testid={`group-row-source-${g.id}`} /></p><p className="t-meta truncate" data-testid={`group-row-leaders-${g.id}`}>{ledBy(g)}</p></div>
            <div className="text-right shrink-0"><p className="t-body">{g.member_count} {g.member_count === 1 ? "person" : "people"}</p><p className="status status-rhythm text-sm justify-end">{g.rhythm_days ? `Every ${g.rhythm_days} days` : "Church default"}</p></div>
          </button>
        ))}
        {groups?.length === 0 && (
          <div className="py-8" data-testid="groups-empty">
            <p className="t-name-sm">{c.leader ? "You aren't leading a Care Group yet." : "No Care Groups yet."}</p>
            <p className="t-body text-ink-soft mt-1">{c.leader ? "When a pastor adds you as a group leader, the group will appear here." : "Create a group to organize people around a care team or shared rhythm."}</p>
          </div>
        )}
      </div>
      {adding && <GroupForm onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

export function Group() {
  const { id } = useParams();
  const { api, membership } = useOrg();
  const c = can(membership);
  const navigate = useNavigate();
  const [g, setG] = useState(null);
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState(false);
  const [people, setPeople] = useState([]);
  const load = () => api(`/groups/${id}`).then(setG).catch((e) => setErr(e.message));
  useEffect(() => { load(); if (c.directory) api("/people").then(setPeople).catch(() => {}); }, [id, api]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = (p, msg) => p.then(() => { toast.success(msg); load(); }).catch((e) => toast.error(e.message));
  const remove = () => window.confirm(`Delete ${g.name}? People stay in Care.`) && api(`/groups/${id}`, { method: "DELETE" }).then(() => { toast("Group deleted"); navigate("/groups"); }).catch((e) => toast.error(e.message));
  const unlink = () => window.confirm(`Stop following the Planning Center list for ${g.name}? Current members stay, and you can manage them in Care.`) && act(api(`/integrations/pco/groups/${id}/unlink`, { method: "POST" }), "Group now managed in Care");
  if (err) return <div data-testid="group-not-found"><p className="t-greeting">We couldn't find that group.</p><Link to="/groups" className="btn btn-primary mt-8">Back to groups</Link></div>;
  if (!g) return <p className="t-meta">Loading…</p>;
  const inGroup = new Set(g.members.map((m) => m.id));
  return (
    <div data-testid="group-screen">
      <Link to="/groups" className="inline-flex items-center gap-2 t-meta hover:text-ink" data-testid="group-back"><ArrowLeft size={16} /> Care Groups</Link>
      <h1 className="t-greeting mt-6" data-testid="group-name">{g.name}</h1>
      {g.description && <p className="t-lead mt-3">{g.description}</p>}
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
        <span className="status status-rhythm" data-testid="group-rhythm">{g.rhythm_days ? `Every ${g.rhythm_days} days` : "Uses church default rhythm"}</span>
        <span className="t-meta" data-testid="group-leaders">{ledBy(g)}</span>
        <span className="t-meta" data-testid="group-source">{g.source === "care" ? "Created in Care" : "Members come from a Planning Center list"}</span>
      </div>
      {c.directory && <div className="flex gap-2 mt-6"><button onClick={() => setEdit(true)} className="btn btn-outline btn-sm" data-testid="group-edit-btn">Edit group</button>{fromPco(g) ? c.manage && <button onClick={unlink} className="btn btn-quiet btn-sm" data-testid="group-unlink-btn">Manage members in Care instead</button> : <button onClick={remove} className="btn btn-quiet btn-sm" data-testid="group-delete-btn">Delete</button>}</div>}
      <section className="mt-10">
        <h2 className="t-heading">{g.members.length} {g.members.length === 1 ? "person" : "people"}</h2>
        {c.directory && g.source === "care" && (
          <Select value="" onChange={(pid) => pid && act(api(`/groups/${id}/members`, { method: "POST", body: { person_id: pid } }), "Added to group")} testid="group-add-member" className="mt-4 !w-auto">
            <option value="">+ Add someone</option>
            {people.filter((p) => !inGroup.has(p.id)).map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </Select>
        )}
        <div className="rule-list border-y border-line mt-4" data-testid="group-members">
          {g.members.map((m) => (
            <div key={m.id} className="py-4 flex items-center justify-between gap-3" data-testid={`group-member-${m.id}`}>
              <Link to={`/people/${m.id}`} className="min-w-0"><p className="t-name-sm truncate">{displayName(m)}</p><p className="t-meta truncate">{m.caregiver ? `Cared for by ${m.caregiver.name}` : "No caregiver yet"}</p></Link>
              <div className="flex items-center gap-3 shrink-0">
                {c.seesCare && <CareState state={m.care_state} />}
                {c.directory && g.source === "care" && <button onClick={() => act(api(`/groups/${id}/members/${m.id}`, { method: "DELETE" }), "Removed")} className="btn btn-quiet btn-sm" data-testid={`group-member-remove-${m.id}`}>Remove</button>}
              </div>
            </div>
          ))}
        </div>
      </section>
      {edit && <GroupForm group={g} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); }} />}
    </div>
  );
}
